import React from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import BreadcrumbItem from "../../shared/components/BreadcrumbItem";
import { SettingsService } from "./service";
import { ISetting } from "../../shared/interfaces/setting/ISetting";
import { handleError } from "../../shared/functions/handleError";

const settingsSchema = yup.object().shape({
  smsApiKey: yup.string().trim(),
  smsEnabled: yup.boolean(),
});

const Settings = () => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ISetting>({
    resolver: yupResolver(settingsSchema) as any,
    defaultValues: async () => {
      const response = await SettingsService.get();
      return response?.data;
    },
  });

  const onSubmit = (data: ISetting) => {
    SettingsService.update(data)
      .then(() => {
        enqueueSnackbar(t("settings.successUpdate"), { variant: "success" });
      })
      .catch(handleError);
  };

  return (
    <React.Fragment>
      <BreadcrumbItem
        mainTitle={t("settings.title")}
        subTitle={t("settings.title")}
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <Card.Header>
            <h5>{t("settings.settingsInformation")}</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("settings.smsApiKey")}</Form.Label>
                  <Controller
                    name="smsApiKey"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        placeholder={t("settings.smsApiKeyPlaceholder")}
                        isInvalid={!!errors.smsApiKey?.message}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.smsApiKey?.message &&
                      t(errors.smsApiKey.message as string)}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Controller
                    name="smsEnabled"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <Form.Check
                        ref={ref}
                        type="switch"
                        id="smsEnabled"
                        label={t("settings.smsEnabled")}
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
          <Card.Footer className="text-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("button.submitting") : t("button.save")}
            </Button>
          </Card.Footer>
        </Card>
      </Form>
    </React.Fragment>
  );
};

export default Settings;
